/* ==========================================================================
   GYMJET — global.js
   Dependency-free. Loaded with `defer` on every page.
   ========================================================================== */
(() => {
  'use strict';

  const GJ = (window.GJ = window.GJ || {});
  const cfg = () => GJ.config || { routes: {}, strings: {} };
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const root = document.documentElement;
  const isRTL = () => root.dir === 'rtl';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motionOK = () => root.classList.contains('motion') && !reduceMotion.matches;
  const isLite = () => root.classList.contains('motion-lite');

  window.GJ_READY = true;

  /* ---------- Utilities ---------- */
  const debounce = (fn, ms = 200) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  };

  const rafThrottle = (fn) => {
    let queued = false;
    return (...args) => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        fn(...args);
      });
    };
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  GJ.utils = { debounce, rafThrottle, parseHTML, $, $$ };

  function formatMoney(cents) {
    const c = cfg();
    const value = (Number(cents) || 0) / 100;
    let [int, dec] = value.toFixed(2).split('.');
    int = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const amount = c.hideZeroDecimals && dec === '00' ? int : `${int}.${dec}`;
    if (c.priceFormat === 'store') {
      return (c.moneyFormat || '{{amount}}').replace(/\{\{\s*amount[a-z_]*\s*\}\}/, amount);
    }
    return `<span class="money">${(c.strings.currency || '[amount]').replace('[amount]', amount)}</span>`;
  }
  GJ.formatMoney = formatMoney;

  /* ---------- Toast ---------- */
  function toast(message, { duration = 3200 } = {}) {
    const region = $('[data-toast-region]');
    if (!region || !message) return;
    const el = document.createElement('div');
    el.className = 'toast';
    el.setAttribute('role', 'status');
    el.innerHTML = message;
    region.replaceChildren(el);
    clearTimeout(toast.timer);
    toast.timer = setTimeout(() => {
      el.classList.add('is-leaving');
      setTimeout(() => el.remove(), 300);
    }, duration);
  }
  GJ.toast = toast;

  /* ---------- Focus trap ---------- */
  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

  function focusables(container) {
    return $$(FOCUSABLE, container).filter((el) => !el.closest('[inert]') && el.getClientRects().length > 0);
  }

  function trapFocus(event, container) {
    const list = focusables(container);
    if (!list.length) return;
    const first = list[0];
    const last = list[list.length - 1];
    if (event.shiftKey && (document.activeElement === first || !container.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- Drawers (menu, search, cart, filters, size guide) ---------- */
  const Drawer = {
    active: null,
    trigger: null,

    open(id, trigger) {
      const drawer = typeof id === 'string' ? document.getElementById(id) : id;
      if (!drawer) return;
      if (this.active && this.active !== drawer) this.close(this.active, { restoreFocus: false });
      this.trigger = trigger || document.activeElement;
      this.active = drawer;
      drawer.removeAttribute('inert');
      drawer.classList.add('is-open');
      document.body.classList.add('is-locked');
      $$(`[aria-controls="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      Header.forceShow(true);
      Mega.closeAll();
      const target = $('[data-autofocus]', drawer) || $('.drawer__panel', drawer);
      if (target) target.focus({ preventScroll: true });
      drawer.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    },

    close(drawer, { restoreFocus = true } = {}) {
      drawer = typeof drawer === 'string' ? document.getElementById(drawer) : drawer || this.active;
      if (!drawer || !drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('inert', '');
      $$(`[aria-controls="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (this.active === drawer) this.active = null;
      if (!this.active) {
        document.body.classList.remove('is-locked');
        Header.forceShow(false);
      }
      if (restoreFocus && this.trigger && document.contains(this.trigger)) {
        this.trigger.focus({ preventScroll: true });
      }
      drawer.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    },
  };
  GJ.drawer = Drawer;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      Drawer.open(opener.dataset.drawerOpen, opener);
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      e.preventDefault();
      Drawer.close(closer.closest('[data-drawer]'));
    }
  });

  document.addEventListener('keydown', (e) => {
    if (!Drawer.active) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      Drawer.close(Drawer.active);
    } else if (e.key === 'Tab') {
      trapFocus(e, Drawer.active);
    }
  });

  /* ---------- Header: sticky, hide on scroll, overlay ---------- */
  const Header = {
    init() {
      this.el = $('[data-header]');
      this.wrap = this.el ? this.el.closest('.section-header') : null;
      if (!this.el || !this.wrap) return;
      this.mode = this.el.dataset.sticky || 'smart';
      this.wrap.dataset.stickyMode = this.mode;
      this.forced = false;
      this.lastY = window.scrollY;
      this.measure();
      window.addEventListener('scroll', rafThrottle(() => this.update()), { passive: true });
      window.addEventListener('resize', debounce(() => this.measure(), 150));
      this.update();
    },

    measure() {
      if (!this.el) return;
      this.height = this.el.offsetHeight;
      const hero = $('#MainContent > .shopify-section:first-child [data-overlay-hero]');
      if (this.el.hasAttribute('data-overlay') && hero) {
        const rect = hero.getBoundingClientRect();
        this.solidAt = Math.max(8, rect.bottom + window.scrollY - this.height - 24);
      } else {
        this.solidAt = 4;
      }
    },

    update() {
      if (!this.el) return;
      const y = Math.max(0, window.scrollY);
      this.el.classList.toggle('is-scrolled', y > this.solidAt);
      if (this.mode === 'smart' && !this.forced) {
        const delta = y - this.lastY;
        if (y < this.height + 60) this.setHidden(false);
        else if (delta > 8) this.setHidden(true);
        else if (delta < -8) this.setHidden(false);
      }
      this.lastY = y;
    },

    setHidden(hidden) {
      if (!this.wrap || this.wrap.classList.contains('is-hidden') === hidden) return;
      this.wrap.classList.toggle('is-hidden', hidden);
      document.body.classList.toggle('hdr-hidden', hidden);
    },

    forceShow(on) {
      this.forced = on;
      if (on) this.setHidden(false);
    },
  };
  GJ.header = Header;

  /* ---------- Mega menu (hover intent + click + keyboard) ---------- */
  const Mega = {
    openItem: null,
    init() {
      this.header = $('[data-header]');
      this.items = $$('[data-mega-item]');
      this.scrim = $('[data-mega-scrim]');
      if (!this.items.length) return;
      const hover = window.matchMedia('(hover: hover) and (pointer: fine)');
      let openTimer;
      let closeTimer;

      this.items.forEach((li) => {
        const trigger = $('[data-mega-trigger]', li);
        li.addEventListener('mouseenter', () => {
          if (!hover.matches) return;
          clearTimeout(closeTimer);
          clearTimeout(openTimer);
          openTimer = setTimeout(() => this.open(li), this.openItem ? 0 : 90);
        });
        li.addEventListener('mouseleave', () => {
          if (!hover.matches) return;
          clearTimeout(openTimer);
          closeTimer = setTimeout(() => {
            if (this.openItem === li) this.close(li);
          }, 180);
        });
        trigger.addEventListener('click', () => {
          if (li.classList.contains('is-open')) this.close(li);
          else this.open(li);
        });
        li.addEventListener('focusout', (e) => {
          if (!li.contains(e.relatedTarget) && this.openItem === li) this.close(li);
        });
        li.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.openItem === li) {
            e.stopPropagation();
            this.close(li);
            trigger.focus();
          }
        });
      });

      document.addEventListener('click', (e) => {
        if (this.openItem && !this.openItem.contains(e.target)) this.close(this.openItem);
      });
    },

    open(li) {
      if (this.openItem === li) return;
      if (this.openItem) this.close(this.openItem, true);
      li.classList.add('is-open');
      $('[data-mega-trigger]', li).setAttribute('aria-expanded', 'true');
      this.openItem = li;
      if (this.header) this.header.classList.add('is-active');
      if (this.scrim && this.header) {
        this.scrim.style.setProperty('--mega-top', `${this.header.getBoundingClientRect().bottom}px`);
        this.scrim.classList.add('is-visible');
      }
    },

    close(li, swapping = false) {
      li.classList.remove('is-open');
      const trigger = $('[data-mega-trigger]', li);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (this.openItem === li) this.openItem = null;
      if (!swapping) {
        if (this.header) this.header.classList.remove('is-active');
        if (this.scrim) this.scrim.classList.remove('is-visible');
      }
    },

    closeAll() {
      if (this.openItem) this.close(this.openItem);
    },
  };

  /* ---------- Mobile menu levels ---------- */
  function initMenuDrawer() {
    const drawer = $('#MenuDrawer');
    if (!drawer) return;
    const rootLevel = $('[data-menu-root]', drawer);

    const reset = () => {
      $$('[data-menu-sub]', drawer).forEach((sub) => {
        sub.classList.remove('is-active');
        sub.setAttribute('inert', '');
      });
      $$('[data-menu-open]', drawer).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (rootLevel) {
        rootLevel.classList.remove('is-covered');
        rootLevel.removeAttribute('inert');
      }
    };

    drawer.addEventListener('click', (e) => {
      const openBtn = e.target.closest('[data-menu-open]');
      if (openBtn) {
        const sub = document.getElementById(openBtn.dataset.menuOpen);
        if (!sub) return;
        sub.removeAttribute('inert');
        sub.classList.add('is-active');
        openBtn.setAttribute('aria-expanded', 'true');
        rootLevel.classList.add('is-covered');
        rootLevel.setAttribute('inert', '');
        const back = $('[data-menu-back]', sub);
        if (back) setTimeout(() => back.focus({ preventScroll: true }), 60);
        return;
      }
      const backBtn = e.target.closest('[data-menu-back]');
      if (backBtn) {
        const sub = backBtn.closest('[data-menu-sub]');
        sub.classList.remove('is-active');
        sub.setAttribute('inert', '');
        rootLevel.classList.remove('is-covered');
        rootLevel.removeAttribute('inert');
        const trigger = $(`[data-menu-open="${sub.id}"]`, rootLevel);
        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
          trigger.focus({ preventScroll: true });
        }
      }
    });

    drawer.addEventListener('drawer:close', () => setTimeout(reset, 380));
  }

  /* ---------- Predictive search ---------- */
  function initSearch() {
    const drawer = $('#SearchDrawer');
    if (!drawer) return;
    const input = $('[data-predictive-input]', drawer);
    const results = $('[data-predictive-results]', drawer);
    const suggest = $('[data-search-suggest]', drawer);
    if (!input) return;
    input.setAttribute('data-autofocus', '');
    if (!cfg().predictiveSearch || !results) return;

    let controller;
    const run = debounce(async () => {
      const q = input.value.trim();
      if (!q) {
        results.innerHTML = '';
        if (suggest) suggest.hidden = false;
        return;
      }
      if (controller) controller.abort();
      controller = new AbortController();
      try {
        const signal = controller.signal;
        const params = new URLSearchParams({
          q,
          'resources[type]': 'product,collection,query',
          'resources[limit]': '8',
          'resources[options][unavailable_products]': 'last',
          section_id: 'predictive-search',
        });
        let res = await fetch(`${cfg().routes.predictiveSearch}?${params}`, { signal });
        let doc = parseHTML(res.ok ? await res.text() : '');
        /* Predictive search doesn't index every language (e.g. Arabic): fall back to full search */
        if (!$('[data-predictive-content] [data-product-card]', doc)) {
          const full = new URLSearchParams({ q, type: 'product', 'options[prefix]': 'last', section_id: 'predictive-search' });
          res = await fetch(`${cfg().routes.search}?${full}`, { signal });
          if (res.ok) doc = parseHTML(await res.text());
        }
        const content = $('[data-predictive-content]', doc);
        results.innerHTML = content ? content.innerHTML : '';
        if (suggest) suggest.hidden = true;
        initReveal(results);
        Wish.sync();
      } catch (err) {
        if (err.name !== 'AbortError') results.innerHTML = '';
      }
    }, 220);

    input.addEventListener('input', run);
  }

  /* ---------- Cart (AJAX + section rendering) ---------- */
  const Cart = {
    sectionIds() {
      const ids = $$('[data-cart-section]').map((el) => el.dataset.cartSection);
      return [...new Set(ids)];
    },

    async request(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.sectionIds().join(','), sections_url: window.location.pathname }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || cfg().strings.cartError);
      }
      return data;
    },

    async add(items) {
      const data = await this.request(`${cfg().routes.cartAdd}.js`, { items });
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      return data;
    },

    async change(line, quantity) {
      const data = await this.request(`${cfg().routes.cartChange}.js`, { line, quantity });
      this.render(data.sections);
      if (typeof data.item_count === 'number') this.updateCount(data.item_count);
      return data;
    },

    render(sections) {
      if (!sections) return;
      const active = document.activeElement;
      const activeKey = active && active.closest('[data-line]') ? active.closest('[data-line]').dataset.key : null;

      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parseHTML(html);
        const fresh = $(`[data-cart-section="${id}"]`, doc);
        if (!fresh) return;
        $$(`[data-cart-section="${id}"]`).forEach((region) => {
          const oldBar = $('.ship-bar', region);
          const from = oldBar ? oldBar.style.getPropertyValue('--progress') : '0';
          region.innerHTML = fresh.innerHTML;
          animateShipBar(region, from);
          Wish.sync();
        });
        const countHolder = $('[data-cart-count-value]', doc);
        if (countHolder) this.updateCount(parseInt(countHolder.dataset.cartCountValue, 10));
      });

      if (activeKey) {
        const again = $(`[data-line][data-key="${CSS.escape(activeKey)}"] [data-qty-input]`);
        if (again) again.focus({ preventScroll: true });
        else if (Drawer.active) $('.drawer__panel', Drawer.active).focus({ preventScroll: true });
      }
      document.dispatchEvent(new CustomEvent('cart:updated'));
    },

    updateCount(count) {
      if (Number.isNaN(count)) return;
      $$('[data-cart-count]').forEach((el) => {
        const prev = el.textContent.trim();
        el.textContent = count;
        el.dataset.count = count;
        if (String(count) !== prev && motionOK()) {
          el.classList.remove('is-bumped');
          void el.offsetWidth;
          el.classList.add('is-bumped');
        }
      });
    },

    open(trigger) {
      if (cfg().cartType === 'drawer' && $('#CartDrawer')) {
        Drawer.open('CartDrawer', trigger);
        animateShipBar($('#CartDrawer'), '0');
      } else {
        window.location.href = cfg().routes.cart;
      }
    },

    async refreshCount() {
      try {
        const res = await fetch(`${cfg().routes.cart}.js`, { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        this.updateCount(cart.item_count);
      } catch (e) {
        /* offline or blocked: keep current count */
      }
    },
  };
  GJ.cart = Cart;

  function animateShipBar(scope, from) {
    const bar = scope ? $('.ship-bar', scope) : null;
    if (!bar) return;
    const target = bar.dataset.progress || bar.style.getPropertyValue('--progress') || '0';
    if (!motionOK()) {
      bar.style.setProperty('--progress', target);
      return;
    }
    bar.style.setProperty('--progress', from || '0');
    requestAnimationFrame(() => requestAnimationFrame(() => bar.style.setProperty('--progress', target)));
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cart-open]');
    if (!opener || cfg().cartType !== 'drawer' || !$('#CartDrawer')) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    Cart.open(opener);
  });

  async function updateLine(line, quantity) {
    if (!line) return;
    line.classList.add('is-updating');
    try {
      await Cart.change(parseInt(line.dataset.line, 10), quantity);
    } catch (err) {
      line.classList.remove('is-updating');
      toast(err.message);
    }
  }

  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty-step]');
    const remove = e.target.closest('[data-line-remove]');
    if (!step && !remove) return;
    const line = (step || remove).closest('[data-line]');
    if (!line) return;
    e.preventDefault();
    const input = $('[data-qty-input]', line);
    const current = parseInt(input ? input.value : '1', 10) || 0;
    const next = remove ? 0 : Math.max(0, current + parseInt(step.dataset.qtyStep, 10));
    if (input) input.value = next;
    updateLine(line, next);
  });

  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-qty-input]');
    if (!input) return;
    const value = Math.max(0, parseInt(input.value, 10) || 0);
    updateLine(input.closest('[data-line]'), value);
  });

  /* ---------- Discount codes in the bag (native Cart API, stackable) ---------- */
  const appliedCodes = (scope) => $$('[data-discount-code]', scope).map((el) => el.dataset.discountCode);

  const discountRequest = (codes) =>
    Cart.request(`${cfg().routes.cartUpdate}.js`, { discount: codes.join(',') });

  /* Locale strings arrive HTML-escaped (e.g. &#39;) — decode before using as text */
  const decodeEntities = (value) => {
    const el = document.createElement('textarea');
    el.innerHTML = value || '';
    return el.value;
  };

  function showDiscountError(region, message) {
    const form = $('[data-discount-form]', region);
    if (!form) return;
    const details = form.closest('[data-discount-details]');
    if (details) details.open = true;
    const error = $('[data-discount-error]', form);
    form.classList.add('is-error');
    if (error) {
      error.textContent = decodeEntities(message);
      error.hidden = false;
    }
    const input = $('input[name="discount"]', form);
    if (input) {
      input.setAttribute('aria-invalid', 'true');
      input.focus({ preventScroll: true });
    }
  }

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-discount-form]');
    if (!form) return;
    e.preventDefault();
    const s = cfg().strings;
    const region = form.closest('[data-cart-section]') || document;
    const input = $('input[name="discount"]', form);
    const button = $('[data-discount-apply]', form);
    const code = (input.value || '').trim();
    form.classList.remove('is-error');
    input.removeAttribute('aria-invalid');
    $('[data-discount-error]', form).hidden = true;

    if (!code) {
      showDiscountError(region, s.discountEmpty);
      return;
    }
    const current = appliedCodes(region);
    if (current.some((c) => c.toLowerCase() === code.toLowerCase())) {
      input.value = '';
      return;
    }

    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    try {
      const data = await discountRequest([...current, code]);
      const entry = (data.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
      if (entry && entry.applicable) {
        Cart.render(data.sections);
        toast(s.discountApplied);
      } else {
        /* Not valid, not eligible, or can't combine: restore the previous codes */
        const restored = await discountRequest(current);
        Cart.render(restored.sections);
        showDiscountError(region, s.discountInvalid);
      }
    } catch (err) {
      button.classList.remove('is-loading');
      showDiscountError(region, err.message || s.cartError);
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  });

  document.addEventListener('click', async (e) => {
    const remove = e.target.closest('[data-discount-remove]');
    if (!remove) return;
    e.preventDefault();
    const region = remove.closest('[data-cart-section]') || document;
    const chip = remove.closest('[data-discount-code]');
    if (chip) chip.style.opacity = '0.4';
    const remaining = appliedCodes(region).filter((c) => c !== remove.dataset.discountRemove);
    try {
      const data = await discountRequest(remaining);
      Cart.render(data.sections);
    } catch (err) {
      if (chip) chip.style.opacity = '';
      toast(err.message);
    }
  });

  /* Quick add: any element with data-add-variant adds one unit */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-add-variant]');
    if (!btn || btn.disabled || btn.classList.contains('is-loading')) return;
    e.preventDefault();
    const id = parseInt(btn.dataset.addVariant, 10);
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    try {
      await Cart.add([{ id, quantity: 1 }]);
      const card = btn.closest('[data-product-card]');
      if (card) closeQuick(card);
      if (Drawer.active && Drawer.active.id === 'CartDrawer') {
        toast(cfg().strings.addedToBag);
      } else {
        Cart.open(btn);
      }
    } catch (err) {
      toast(err.message);
    } finally {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    }
  });

  /* Touch quick-add toggle on cards */
  function closeQuick(card) {
    card.classList.remove('is-quick-open');
    const t = $('[data-quick-toggle]', card);
    if (t) t.setAttribute('aria-expanded', 'false');
  }

  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-quick-toggle]');
    if (toggle) {
      e.preventDefault();
      const card = toggle.closest('[data-product-card]');
      const open = !card.classList.contains('is-quick-open');
      $$('.card.is-quick-open').forEach((c) => c !== card && closeQuick(c));
      card.classList.toggle('is-quick-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      return;
    }
    if (!e.target.closest('[data-quick-panel]')) $$('.card.is-quick-open').forEach(closeQuick);
  });

  /* ---------- Wishlist (device storage) ---------- */
  const Wish = {
    key: 'gj_wishlist',
    get() {
      try {
        const list = JSON.parse(localStorage.getItem(this.key));
        return Array.isArray(list) ? list : [];
      } catch (e) {
        return [];
      }
    },
    set(list) {
      try {
        localStorage.setItem(this.key, JSON.stringify(list.slice(0, 60)));
      } catch (e) {
        /* storage blocked */
      }
      this.sync();
      document.dispatchEvent(new CustomEvent('wishlist:change'));
    },
    toggle(handle) {
      const list = this.get();
      const i = list.indexOf(handle);
      if (i > -1) list.splice(i, 1);
      else list.unshift(handle);
      this.set(list);
      return i === -1;
    },
    sync() {
      const list = this.get();
      $$('[data-wish]').forEach((b) => b.setAttribute('aria-pressed', String(list.includes(b.dataset.wish))));
      $$('[data-wish-count]').forEach((c) => {
        c.textContent = list.length ? list.length : '';
        c.dataset.count = list.length;
      });
    },
  };
  GJ.wishlist = Wish;

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wish]');
    if (!btn) return;
    e.preventDefault();
    const added = Wish.toggle(btn.dataset.wish);
    if (motionOK()) {
      btn.classList.remove('is-popping');
      void btn.offsetWidth;
      btn.classList.add('is-popping');
    }
    const s = cfg().strings;
    const link = cfg().wishlistUrl ? ` <a href="${cfg().wishlistUrl}" style="text-decoration:underline">${s.wishlistView || '→'}</a>` : '';
    toast(added ? `${s.wishlistAdded}${link}` : s.wishlistRemoved);
  });

  window.addEventListener('storage', (e) => {
    if (e.key === Wish.key) Wish.sync();
  });

  /* Wishlist page: cards are fetched via the product "card" view */
  async function initWishlistPage(scope = document) {
    const page = $('[data-wishlist-page]', scope);
    if (!page) return;
    const grid = $('[data-wishlist-grid]', page);
    const empty = $('[data-wishlist-empty]', page);
    const list = Wish.get();
    if (!list.length) {
      grid.innerHTML = '';
      empty.hidden = false;
      return;
    }
    const base = (cfg().routes.root || '/').replace(/\/$/, '');
    const cards = await Promise.all(
      list.map((h) =>
        fetch(`${base}/products/${encodeURIComponent(h)}?view=card`)
          .then((r) => (r.ok ? r.text() : ''))
          .catch(() => '')
      )
    );
    grid.innerHTML = cards
      .map((c) => c.trim())
      .filter((c) => c.indexOf('data-product-card') > -1)
      .join('');
    empty.hidden = grid.children.length > 0;
    initReveal(page);
    Wish.sync();
  }

  document.addEventListener('wishlist:change', () => {
    const page = $('[data-wishlist-page]');
    if (!page) return;
    const list = Wish.get();
    $$('[data-product-card]', page).forEach((card) => {
      if (!list.includes(card.dataset.handle)) card.remove();
    });
    const grid = $('[data-wishlist-grid]', page);
    $('[data-wishlist-empty]', page).hidden = grid.children.length > 0;
  });

  /* ---------- Language choice memory ---------- */
  document.addEventListener('click', (e) => {
    const choice = e.target.closest('[data-lang-choice]');
    if (choice) {
      try {
        localStorage.setItem('gj_lang', choice.dataset.langChoice);
      } catch (err) {
        /* ignore */
      }
    }
    if (e.target.closest('[data-lang-dismiss]')) {
      try {
        localStorage.setItem('gj_lang', cfg().locale || 'en');
      } catch (err) {
        /* ignore */
      }
      root.classList.remove('suggest-ar');
    }
  });

  /* ---------- Announcement bar ---------- */
  function initAnnounce(scope = document) {
    $$('[data-announce]', scope).forEach((bar) => {
      if (bar.dataset.ready) return;
      bar.dataset.ready = '1';
      const slides = $$('[data-announce-slide]', bar);
      if (slides.length < 2) return;
      let index = 0;
      let timer;
      const interval = parseInt(bar.dataset.interval, 10) || 5000;
      const show = (n) => {
        const current = slides[index];
        current.classList.remove('is-active');
        current.classList.add('is-leaving');
        setTimeout(() => current.classList.remove('is-leaving'), 500);
        index = (n + slides.length) % slides.length;
        slides[index].classList.add('is-active');
      };
      const stop = () => clearInterval(timer);
      const play = () => {
        stop();
        if (!reduceMotion.matches) timer = setInterval(() => show(index + 1), interval);
      };
      const prev = $('[data-announce-prev]', bar);
      const next = $('[data-announce-next]', bar);
      if (prev) prev.addEventListener('click', () => { show(index - 1); play(); });
      if (next) next.addEventListener('click', () => { show(index + 1); play(); });
      bar.addEventListener('mouseenter', stop);
      bar.addEventListener('mouseleave', play);
      bar.addEventListener('focusin', stop);
      bar.addEventListener('focusout', play);
      document.addEventListener('visibilitychange', () => (document.hidden ? stop() : play()));
      play();
    });
  }

  /* ---------- Reveal on scroll ---------- */
  let revealObserver;

  function splitWords(scope) {
    $$('[data-split]:not([data-split-done])', scope).forEach((el) => {
      el.setAttribute('data-split-done', '');
      let wi = 0;
      const walk = (node) => {
        Array.from(node.childNodes).forEach((child) => {
          if (child.nodeType === Node.TEXT_NODE) {
            const parts = child.textContent.split(/(\s+)/);
            const frag = document.createDocumentFragment();
            parts.forEach((part) => {
              if (!part) return;
              if (/^\s+$/.test(part)) {
                frag.appendChild(document.createTextNode(part));
                return;
              }
              const outer = document.createElement('span');
              outer.className = 'split-w';
              const inner = document.createElement('span');
              inner.textContent = part;
              inner.style.setProperty('--wi', wi++);
              outer.appendChild(inner);
              frag.appendChild(outer);
            });
            child.replaceWith(frag);
          } else if (child.nodeType === Node.ELEMENT_NODE && child.tagName !== 'BR') {
            walk(child);
          }
        });
      };
      walk(el);
    });
  }

  function initReveal(scope = document) {
    const selector = '[data-reveal]:not(.is-in), [data-reveal-group]:not(.is-in), [data-split]:not(.is-in), .footer-wordmark:not(.is-in)';
    const targets = $$(selector, scope);
    if (!targets.length) return;
    if (!motionOK() || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-in'));
      return;
    }
    splitWords(scope);
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-in');
              revealObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0 }
      );
    }
    targets.forEach((el) => {
      if (el.hasAttribute('data-reveal-group')) {
        Array.from(el.children).forEach((child, i) => child.style.setProperty('--i', i % 12));
      }
      revealObserver.observe(el);
    });
  }
  GJ.reveal = initReveal;

  /* ---------- Hero intro ---------- */
  function initHero(scope = document) {
    $$('[data-hero]', scope).forEach((hero) => {
      if (hero.classList.contains('is-loaded')) return;
      splitWords(hero);
      const go = () => hero.classList.add('is-loaded');
      if (!motionOK()) return go();
      const fonts = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
      Promise.race([fonts, new Promise((r) => setTimeout(r, 700))]).then(() => setTimeout(go, 30));
    });
  }

  /* ---------- Parallax ---------- */
  function initParallax() {
    if (!motionOK() || isLite() || !cfg().parallax) return;
    const items = $$('[data-parallax]');
    if (!items.length || !('IntersectionObserver' in window)) return;
    const active = new Set();
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) active.add(entry.target);
          else active.delete(entry.target);
          entry.target.classList.toggle('is-parallaxing', entry.isIntersecting);
        }),
      { rootMargin: '15% 0px' }
    );
    items.forEach((el) => io.observe(el));
    const tick = () => {
      const vh = window.innerHeight;
      active.forEach((el) => {
        const inner = $('[data-parallax-inner]', el);
        if (!inner) return;
        const rect = el.getBoundingClientRect();
        const speed = parseFloat(el.dataset.parallax) || 0.12;
        const offset = (rect.top + rect.height / 2 - vh / 2) * -speed;
        inner.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
      });
    };
    window.addEventListener('scroll', rafThrottle(tick), { passive: true });
    window.addEventListener('resize', rafThrottle(tick));
    tick();
  }

  /* ---------- Scroll-lit words (manifesto) ---------- */
  function initScrollWords(scope = document) {
    const blocks = $$('[data-scroll-words]', scope);
    if (!blocks.length) return;
    blocks.forEach((el) => {
      if (el.dataset.swReady) return;
      el.dataset.swReady = '1';
      const walk = (node) => {
        Array.from(node.childNodes).forEach((child) => {
          if (child.nodeType === Node.TEXT_NODE) {
            const frag = document.createDocumentFragment();
            child.textContent.split(/(\s+)/).forEach((part) => {
              if (!part) return;
              if (/^\s+$/.test(part)) frag.appendChild(document.createTextNode(part));
              else {
                const span = document.createElement('span');
                span.className = 'sw';
                span.textContent = part;
                frag.appendChild(span);
              }
            });
            child.replaceWith(frag);
          } else if (child.nodeType === Node.ELEMENT_NODE && child.tagName !== 'BR') walk(child);
        });
      };
      walk(el);
      el._words = $$('.sw', el);
    });
    if (!motionOK() || isLite()) {
      blocks.forEach((el) => el._words.forEach((w) => w.classList.add('is-lit')));
      return;
    }
    const active = new Set();
    const io = new IntersectionObserver((entries) =>
      entries.forEach((entry) => (entry.isIntersecting ? active.add(entry.target) : active.delete(entry.target)))
    );
    blocks.forEach((el) => io.observe(el));
    const update = () => {
      const vh = window.innerHeight;
      active.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const start = vh * 0.88;
        const end = vh * 0.4;
        const progress = Math.min(1, Math.max(0, (start - rect.top) / (rect.height + start - end)));
        const lit = Math.round(progress * el._words.length);
        if (lit !== el._lit) {
          el._lit = lit;
          el._words.forEach((w, i) => w.classList.toggle('is-lit', i < lit));
        }
      });
    };
    window.addEventListener('scroll', rafThrottle(update), { passive: true });
    update();
  }

  /* ---------- Sliders ---------- */
  function initSliders(scope = document) {
    $$('[data-slider]', scope).forEach((wrap) => {
      if (wrap.dataset.sliderReady || wrap.closest('[hidden]')) return;
      wrap.dataset.sliderReady = '1';
      const track = $('[data-slider-track]', wrap);
      if (!track) return;
      const prev = $('[data-slider-prev]', wrap);
      const next = $('[data-slider-next]', wrap);
      const progress = $('[data-slider-progress]', wrap);

      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const x = Math.abs(track.scrollLeft);
        const ratio = track.scrollWidth > 0 ? Math.min(1, track.clientWidth / track.scrollWidth) : 1;
        if (progress) {
          progress.style.setProperty('--thumb', `${(ratio * 100).toFixed(2)}%`);
          progress.style.setProperty('--thumb-ratio', ratio.toFixed(4));
          progress.style.setProperty('--p', max > 0 ? (x / max).toFixed(4) : '0');
        }
        if (prev) prev.disabled = x <= 2;
        if (next) next.disabled = x >= max - 2;
        wrap.classList.toggle('is-static', max <= 2);
      };

      const step = (dir) => {
        const first = track.children[0];
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const size = first ? first.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
        const perPage = Math.max(1, Math.floor((track.clientWidth + gap) / size));
        track.scrollBy({ left: dir * size * perPage * (isRTL() ? -1 : 1), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      };

      if (prev) prev.addEventListener('click', () => step(-1));
      if (next) next.addEventListener('click', () => step(1));
      track.addEventListener('scroll', rafThrottle(update), { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(rafThrottle(update)).observe(track);
      update();
    });
  }
  GJ.sliders = initSliders;

  /* ---------- Marquee: pause when off-screen ---------- */
  function initMarquees(scope = document) {
    const items = $$('[data-marquee]', scope);
    if (!items.length || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) =>
      entries.forEach((entry) => entry.target.classList.toggle('is-paused', !entry.isIntersecting))
    );
    items.forEach((m) => io.observe(m));
  }

  /* ---------- Shared-element page transition (card → product) ---------- */
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-product-card] a');
    if (!link || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (!('startViewTransition' in document) || reduceMotion.matches) return;
    $$('.card__media').forEach((m) => (m.style.viewTransitionName = ''));
    const media = $('.card__media', link.closest('[data-product-card]'));
    if (media) media.style.viewTransitionName = 'gj-media';
  });

  window.addEventListener('pageshow', (e) => {
    $$('.card__media').forEach((m) => (m.style.viewTransitionName = ''));
    if (e.persisted) {
      Cart.refreshCount();
      Wish.sync();
    }
  });

  /* ---------- Init ---------- */
  function initScope(scope = document) {
    initReveal(scope);
    initSliders(scope);
    initMarquees(scope);
    initHero(scope);
    initAnnounce(scope);
    Wish.sync();
  }

  Header.init();
  Mega.init();
  initMenuDrawer();
  initSearch();
  initScope(document);
  initParallax();
  initScrollWords(document);
  initWishlistPage(document);

  /* Theme editor support */
  document.addEventListener('shopify:section:load', (e) => {
    initScope(e.target);
    initScrollWords(e.target);
    if (e.target.querySelector('[data-header]')) {
      Header.init();
      Mega.init();
      initMenuDrawer();
      initSearch();
    }
    Header.measure();
  });

  document.addEventListener('shopify:section:select', (e) => {
    $$('[data-reveal], [data-reveal-group], [data-split]', e.target).forEach((el) => el.classList.add('is-in'));
  });
})();
